import { isNotUndefined, isRecord, isUndefined } from '@wistia/type-guards';
import { useMemo } from 'react';
import { useActiveMq } from '../../../hooks/useMq';

type Breakpoints =
  | 'isLgAndDown'
  | 'isLgAndUp'
  | 'isMdAndDown'
  | 'isMdAndUp'
  | 'isSmAndDown'
  | 'isSmAndUp'
  | 'isXlAndDown'
  | 'isXlAndUp'
  | 'isXsAndDown'
  | 'isXsAndUp';

type AtLeastOneBreakpoint<T, U = { [K in keyof T]: Pick<T, K> }> = Partial<T> & U[keyof U];

export type ResponsiveObject<T> = AtLeastOneBreakpoint<Record<Breakpoints, T>> & {
  base: T;
};

const isResponsiveObject = <T>(values: unknown): values is ResponsiveObject<T> => {
  return (
    typeof values === 'object' && values !== null && !Array.isArray(values) && 'base' in values
  );
};

/** How to use in a component
 * const Example = ({ someProp }: ExampleProps) => {
 *  const somePropResponsiveValue = useResponsiveProp<ExampleProps['someProp']>(someProp);
 *
 * return <div>{somePropResponsiveValue}</div>;
 * }
 *
 * The end use could implement either:
 * <Example someProp="a normal value" />
 * <Example someProp={{ base: 'baselinevalue',  isMdAndUp: 'value for medium screens', isLgAndUp: 'value for larger screens' }} />
 */
export const useResponsiveProp = <T>(values: ResponsiveObject<T> | T) => {
  const activeMediaQueries = useActiveMq();
  return useMemo(() => {
    if (isRecord(values) && isResponsiveObject(values)) {
      const mq = activeMediaQueries.find((key) => key in values) as Breakpoints | 'base';
      return isNotUndefined(mq) && isNotUndefined(values[mq]) ? values[mq] : values.base;
    }
    return values;
  }, [activeMediaQueries, values]);
};

type ResponsiveProps<ComponentProps> = {
  [K in keyof ComponentProps]: ComponentProps[K] | ResponsiveObject<ComponentProps[K]>;
};

/** How to use in a component
 * const Example = (props: ExampleProps) => {
 *  const propsWithResponsivePropsResolved = useResponsiveProps<ExampleProps>(props, ['someProp', 'someOtherProp']);
 *  or if you want to attempt to resolve all props
 *  const propsWithResponsivePropsResolved = useResponsiveProps<ExampleProps>(props);
 * }
 */
export const useResponsiveProps = <T>(props: ResponsiveProps<T>, allowedKeys?: (keyof T)[]) => {
  const activeMediaQueries = useActiveMq();
  return useMemo(() => {
    const keys = isUndefined(allowedKeys) ? Object.keys(props) : allowedKeys;

    const resolvedProps = {
      ...props,
    };

    keys.forEach((_propKey) => {
      const propKey = _propKey as keyof T;
      const values = props[propKey];
      if (isRecord(values) && isResponsiveObject(values)) {
        const mq = activeMediaQueries.find((key) => key in values) as Breakpoints | 'base';
        resolvedProps[propKey] =
          isNotUndefined(mq) && isNotUndefined(values[mq])
            ? (values[mq] as T[keyof T])
            : (values.base as T[keyof T]);
      }
    });
    return resolvedProps;
  }, [activeMediaQueries, props, allowedKeys]);
};
